import React, { useState } from "react";
import styled from "styled-components";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Styled components copied from Signup.js
const Container = styled.div`
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
`;
const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  @media (max-width: 768px) {
    padding: 20px;
  }
`;
const SignupBox = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 50px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media (max-width: 768px) {
    padding: 30px 20px;
    max-width: 100%;
    border: none;
    box-shadow: none;
  }
`;
const SignupHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;
const SignupSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0 0 40px 0;
  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #000;
  display: block;
`;
const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;
const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s;
  padding-right: ${(props) => (props.hasIcon ? "40px" : "14px")};
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
  &::placeholder {
    color: #999;
  }
  @media (max-width: 768px) {
    padding: 11px 12px;
    font-size: 13px;
  }
`;
const ErrorMessage = styled.span`
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
`;
const SuccessBox = styled.div`
  background: #f0f0f0;
  border-left: 4px solid #000;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #000;
  font-weight: 500;
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;
const SignupButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
    margin-bottom: 18px;
  }
`;
const OTPSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;
const OTPHeading = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #000;
  margin: 0 0 8px 0;
  text-align: center;
`;
const OTPSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  text-align: center;
  margin: 0 0 20px 0;
`;
const OTPInputContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
  @media (max-width: 768px) {
    gap: 10px;
  }
`;
const OTPInput = styled.input`
  width: 60px;
  height: 60px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s;
  font-family: "Courier New", monospace;
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
  &::placeholder {
    color: #ccc;
  }
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;
const VerifyButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;
const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  &:hover {
    color: #666;
  }
`;
const SuccessMessage = styled.span`
  font-size: 12px;
  color: #44cc44;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: phone, 2: otp, 3: password
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [serverOTP, setServerOTP] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Phone validation
  const validatePhone = () => {
    if (!/^\d{10}$/.test(phone)) {
      setPhoneError("Phone number must be 10 digits");
      return false;
    }
    setPhoneError("");
    return true;
  };

  // Password validation
  const validatePassword = () => {
    if (!password || password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError("");
    return true;
  };

  // Submit phone for OTP
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhone()) return;
    setLoading(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone }),
        },
      );
      const data = await resp.json();
      if (
        data.status &&
        data.data &&
        data.data.otp_preview &&
        data.data.otp_preview.otp
      ) {
        setServerOTP(data.data.otp_preview.otp);
        setStep(2);
        setOtpValues(["", "", "", "", "", ""]);
        setOtpError("");
      } else {
        setPhoneError(data.message || "Failed to send OTP.");
      }
    } catch (error) {
      setPhoneError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // OTP input
  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    setOtpError("");
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // OTP verify
  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    const enteredOTP = otpValues.join("");
    if (enteredOTP.length !== 6) {
      setOtpError("Please enter a 6-digit OTP");
      return;
    }
    setOtpLoading(true);
    if (enteredOTP !== serverOTP) {
      setOtpError("Invalid OTP. Please try again.");
      setOtpValues(["", "", "", "", "", ""]);
      setOtpLoading(false);
      return;
    }
    setStep(3);
    setOtpLoading(false);
  };

  // Password reset
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    setLoading(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone,
            otp: serverOTP,
            password,
            password_confirmation: confirmPassword,
          }),
        },
      );
      const data = await resp.json();
      if (data.status) {
        setSuccessMessage(
          "Password changed successfully! Redirecting to login...",
        );
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        setPasswordError(data.message || "Failed to reset password.");
      }
    } catch (error) {
      setPasswordError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* <Navbar /> */}
      <ContentWrapper>
        <SignupBox>
          <SignupHeading>Forgot Password</SignupHeading>
          <SignupSubtitle>Reset your password securely</SignupSubtitle>
          {successMessage && <SuccessBox>{successMessage}</SuccessBox>}
          {step === 1 && (
            <form onSubmit={handlePhoneSubmit}>
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit phone number"
                  maxLength="10"
                  disabled={loading}
                />
                {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
              </FormGroup>
              <SignupButton type="submit" disabled={loading}>
                {loading ? "Sending OTP..." : "Submit"}
              </SignupButton>
            </form>
          )}
          {step === 2 && (
            <form onSubmit={handleOTPSubmit}>
              <OTPSection>
                <OTPHeading>Verify OTP</OTPHeading>
                <OTPSubtitle>
                  Enter the 6-digit OTP sent to <strong>{phone}</strong>.
                </OTPSubtitle>
                <OTPInputContainer>
                  {otpValues.map((value, index) => (
                    <OTPInput
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={value}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      disabled={otpLoading}
                      placeholder="•"
                    />
                  ))}
                </OTPInputContainer>
                {otpError && <ErrorMessage>{otpError}</ErrorMessage>}
                <VerifyButton type="submit" disabled={otpLoading}>
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </VerifyButton>
              </OTPSection>
            </form>
          )}
          {step === 3 && (
            <form onSubmit={handlePasswordSubmit}>
              <FormGroup>
                <Label htmlFor="password">New Password</Label>
                <InputWrapper>
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a new password"
                    hasIcon={true}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    disabled={loading}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </PasswordToggle>
                </InputWrapper>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <InputWrapper>
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    hasIcon={true}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </PasswordToggle>
                </InputWrapper>
                {password &&
                  confirmPassword &&
                  password === confirmPassword && (
                    <SuccessMessage>
                      <CheckOutlined />
                      Passwords match
                    </SuccessMessage>
                  )}
                {passwordError && <ErrorMessage>{passwordError}</ErrorMessage>}
              </FormGroup>
              <SignupButton type="submit" disabled={loading}>
                {loading ? "Changing password..." : "Change Password"}
              </SignupButton>
            </form>
          )}
        </SignupBox>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default ForgotPassword;
